import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Linkedin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const REASONS = [
  { value: "ponencia", label: "Ponencia o evento" },
  { value: "clase", label: "Clase o sesión académica" },
  { value: "advisory", label: "Advisory estratégico" },
  { value: "storytelling", label: "Storytelling corporativo" },
  { value: "medios", label: "Medios / entrevista" },
  { value: "otro", label: "Otro" },
];

const INITIAL = { name: "", company: "", position: "", email: "", reason: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Mensaje enviado correctamente.");
    } catch {
      toast.error("Algo no funcionó. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="pt-16 md:pt-20">
      {/* Header */}
      <section data-testid="contact-header" className="py-20 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-6">Contacto</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-normal leading-[1.1] max-w-2xl mb-6">
            Si tienes un motivo concreto, escribe.
          </h1>
          <p className="text-muted-foreground leading-relaxed max-w-xl">
            Para directivos, fundadores, escuelas de negocio, fondos y medios con algo concreto
            que proponer. Sin formularios genéricos de "hagamos algo juntos".
          </p>
        </div>
      </section>

      {/* Form + info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
            {/* Form */}
            <div className="lg:col-span-2">
              {sent ? (
                <div data-testid="contact-success" className="bg-primary/10 border border-primary/20 p-10">
                  <p className="font-serif text-2xl mb-3 text-primary">Mensaje recibido.</p>
                  <p className="text-muted-foreground leading-relaxed">
                    Gracias por escribir. Si el motivo encaja, te respondo en menos de 48 horas.
                  </p>
                  <button
                    onClick={() => { setForm(INITIAL); setSent(false); }}
                    className="label-mono text-primary mt-6 hover:underline"
                  >
                    Enviar otro mensaje
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-8">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="label-mono text-muted-foreground block mb-2">Nombre *</label>
                      <input
                        className="input-underline"
                        placeholder="Tu nombre"
                        value={form.name}
                        onChange={set("name")}
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="label-mono text-muted-foreground block mb-2">Email *</label>
                      <input
                        type="email"
                        className="input-underline"
                        placeholder="tu@empresa.com"
                        value={form.email}
                        onChange={set("email")}
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                    <div>
                      <label className="label-mono text-muted-foreground block mb-2">Empresa</label>
                      <input
                        className="input-underline"
                        placeholder="Nombre de tu empresa"
                        value={form.company}
                        onChange={set("company")}
                        data-testid="contact-company"
                      />
                    </div>
                    <div>
                      <label className="label-mono text-muted-foreground block mb-2">Cargo</label>
                      <input
                        className="input-underline"
                        placeholder="CEO, Director de…"
                        value={form.position}
                        onChange={set("position")}
                        data-testid="contact-position"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="label-mono text-muted-foreground block mb-3">Motivo del contacto *</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {REASONS.map((r) => (
                        <label
                          key={r.value}
                          data-testid={`reason-${r.value}`}
                          className={`border px-4 py-3 cursor-pointer transition-colors text-xs font-mono tracking-wider uppercase ${
                            form.reason === r.value
                              ? "border-primary bg-primary/5 text-primary"
                              : "border-border text-muted-foreground hover:border-foreground/20"
                          }`}
                        >
                          <input
                            type="radio"
                            name="reason"
                            value={r.value}
                            className="sr-only"
                            onChange={set("reason")}
                            required
                          />
                          {r.label}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="label-mono text-muted-foreground block mb-2">Mensaje *</label>
                    <textarea
                      className="input-underline textarea"
                      placeholder="Describe brevemente el motivo concreto de tu contacto: tipo de evento, fecha aproximada, formato, audiencia, etc."
                      value={form.message}
                      onChange={set("message")}
                      required
                      rows={5}
                      data-testid="contact-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !form.reason}
                    data-testid="contact-submit"
                    className="label-mono bg-primary text-primary-foreground px-10 py-4 hover:bg-primary/90 transition-colors disabled:opacity-50"
                  >
                    {loading ? "Enviando…" : "Enviar mensaje"}
                  </button>
                </form>
              )}
            </div>

            {/* Contact info */}
            <div data-testid="contact-info" className="space-y-8">
              <div>
                <p className="label-mono text-muted-foreground mb-4">Ubicación</p>
                <div className="flex items-center gap-3 text-sm text-muted-foreground">
                  <MapPin size={14} className="text-primary shrink-0" />
                  Palencia / Madrid, España
                </div>
              </div>
              <div>
                <p className="label-mono text-muted-foreground mb-4">LinkedIn</p>
                <a
                  href="https://linkedin.com/in/eduardodelgadolobejon"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Linkedin size={14} className="text-primary shrink-0" />
                  linkedin.com/in/eduardodelgadolobejon
                </a>
              </div>
              <div className="border-t border-border pt-8">
                <p className="font-serif text-lg mb-3">Tiempo de respuesta</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Si el motivo encaja, respondo en menos de 48 horas.
                  Las solicitudes genéricas sin contexto concreto no reciben respuesta.
                </p>
              </div>
              <div className="border-t border-border pt-8">
                <p className="font-serif text-lg mb-3">Qué no acepta</p>
                <ul className="space-y-2 text-xs text-muted-foreground">
                  {["Mentoring abierto sin contexto", "Consultoría de marketing genérica", "Charlas inspiracionales sin contenido", "Solicitudes de colaboración sin presupuesto real"].map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">—</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
